// Cooperative pointer-based photo drag (view=preview only). Native HTML5
// drag-and-drop is unusable here: Chromium does not route drag events —
// nor captured pointer events — across the process boundary between this
// iframe and the embedding editor, so a drag sourced here goes dark the
// moment the cursor leaves the pane (verified live). With NO native drag
// and NO capture, ordinary pointer events route normally to whichever
// document is under the cursor, so the drag is cooperative instead:
//   - this document tracks the pointer while it is over it (start
//     threshold, move stream, slot highlight, local drop resolution);
//   - the HOST tracks it natively over its own territory (rail thumbnails,
//     arrows) and draws the drag ghost;
//   - the host broadcasts photo-drag-sync so the OTHER facing page joins
//     in (highlights + resolves drops that land on it) and so everyone
//     cleans up when the gesture ends elsewhere.
import { markDragJustEnded } from "./photoDrag";
import {
	postPhotoDragMove,
	postPhotoDragRelease,
	postPhotoDragStart,
} from "./postmessage";

const DRAG_THRESHOLD_PX = 6;
const MOVE_POST_INTERVAL_MS = 30;

let pending = null; // {questionId, imgSrc, x, y} after pointerdown on a photo
let dragging = null; // {fromQuestionId, mine} while a drag is live
let lastPost = 0;
let hoverEl = null;

function slotAt(x, y) {
	const el = document.elementFromPoint(x, y);
	return el && el.closest
		? el.closest("[data-editable-type=image][data-editable-id]")
		: null;
}

function setHover(slot) {
	if (hoverEl === slot) return;
	if (hoverEl) hoverEl.classList.remove("photo-drop-target");
	hoverEl = slot || null;
	if (hoverEl) hoverEl.classList.add("photo-drop-target");
}

function setDraggingCursor(active) {
	document.body.style.userSelect = active ? "none" : "";
	document.body.style.cursor = active ? "grabbing" : "";
}

function finishLocal() {
	dragging = null;
	setHover(null);
	setDraggingCursor(false);
}

function onPointerMove(e) {
	if (pending && !dragging) {
		const dist = Math.hypot(e.clientX - pending.x, e.clientY - pending.y);
		if (dist >= DRAG_THRESHOLD_PX) {
			dragging = { fromQuestionId: pending.questionId, mine: true };
			setDraggingCursor(true);
			postPhotoDragStart(pending.questionId, pending.imgSrc);
		}
	}
	if (!dragging) return;
	const now = Date.now();
	if (now - lastPost >= MOVE_POST_INTERVAL_MS) {
		lastPost = now;
		postPhotoDragMove(e.clientX, e.clientY);
	}
	const slot = slotAt(e.clientX, e.clientY);
	setHover(
		slot && slot.getAttribute("data-editable-id") !== dragging.fromQuestionId
			? slot
			: null,
	);
}

function onPointerUp(e) {
	pending = null;
	if (!dragging) return;
	finishLocal();
	markDragJustEnded();
	// The host resolves the drop against its drawn slot rects (its
	// measure-editables geometry) — this document only reports where the
	// pointer was released. The host then broadcasts sync-off.
	postPhotoDragRelease(e.clientX, e.clientY);
}

/** Photo.jsx pointerdown entry: arms a drag; movement past the threshold
 * starts it (a plain click stays a click). */
export function beginPhotoPointerDrag(questionId, imgSrc, e) {
	if (e.pointerType === "mouse" && e.button !== 0) return;
	pending = { questionId, imgSrc, x: e.clientX, y: e.clientY };
}

/** Host broadcast: a drag (possibly sourced in the OTHER pane) is live —
 * join in, or clean up when it ends anywhere. */
export function syncPhotoPointerDrag(active, fromQuestionId) {
	if (active) {
		if (dragging && dragging.mine) return; // we are the source
		dragging = { fromQuestionId: fromQuestionId || null, mine: false };
		setDraggingCursor(true);
	} else {
		pending = null;
		finishLocal();
	}
}

let installed = false;
export function installPhotoPointerDrag() {
	if (installed || typeof document === "undefined") return;
	installed = true;
	document.addEventListener("pointermove", onPointerMove, true);
	document.addEventListener("pointerup", onPointerUp, true);
}
